import {
  TIMESERIES_DATA_SOURCES,
  TIMESERIES_INSTRUMENT_STATUSES,
  TIMESERIES_STALE_DAYS,
  type TimeseriesInstrumentStatus,
  type Translated,
} from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, LineChart } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { setInstrumentSyncEnabled } from "@/lib/api/timeseries-admin";
import { timeseriesAdminKeys, useTimeseriesInstruments } from "@/lib/hooks/use-timeseries-admin";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { daysSince, statusSummary } from "./status-summary";
import { TimeseriesInspectDialog } from "./timeseries-inspect-dialog";
import { T } from "./timeseries-instruments-wrapper.i18n";

const PER_PAGE = 50;
const ALL = "all";

// custom (user-uploaded) and combined (recomputed) have no vendor sync — no
// toggle for them, and no resync (there is nothing to refetch from).
const UNSYNCED_SOURCES = new Set(["custom", "combined"]);

const DOT_COLOR = {
  ok: "bg-success",
  warn: "bg-warning",
  error: "bg-destructive",
} as const;

function StatusDot({ item, lang }: { item: TimeseriesInstrumentStatus; lang: string }) {
  const { level, reasons } = statusSummary(item, lang);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${DOT_COLOR[level]}`} />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        {reasons.map((r) => (
          <div key={r}>{r}</div>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}

function StatusBadges({ item, t }: { item: TimeseriesInstrumentStatus; t: Translated<typeof T> }) {
  const badges: React.ReactNode[] = [];
  if (item.pointCount === 0) {
    badges.push(
      <Badge key="nodata" variant="destructive">
        {t.noData}
      </Badge>,
    );
  }
  if (!item.currency) {
    badges.push(
      <Badge key="nocur" variant="warning">
        {t.noCurrency}
      </Badge>,
    );
  }
  const age = daysSince(item.lastDate);
  if (item.pointCount > 0 && age !== null && age > TIMESERIES_STALE_DAYS) {
    badges.push(
      <Badge key="stale" variant="warning">
        {t.stale} ({age}d)
      </Badge>,
    );
  }
  if (item.syncDisabledAt) {
    badges.push(
      <Badge key="syncoff" variant="destructive">
        {t.syncDisabled} ({item.syncFailures})
      </Badge>,
    );
  } else if (item.syncFailures > 0) {
    badges.push(
      <Badge key="syncfail" variant="warning">
        {t.syncFailing} ({item.syncFailures})
      </Badge>,
    );
  }
  if (item.fxMissing) {
    badges.push(
      <Badge key="fxmissing" variant="warning">
        {t.fxMissing} ({item.currency})
      </Badge>,
    );
  }
  if (badges.length === 0) {
    return <Badge variant="success">{t.ok}</Badge>;
  }
  return <div className="flex flex-wrap gap-1">{badges}</div>;
}

export function TimeseriesInstrumentsWrapper() {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);

  const [source, setSource] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching } = useTimeseriesInstruments({
    source: source === ALL ? undefined : source,
    status: status === ALL ? undefined : status,
    search: search || undefined,
    page,
    perPage: PER_PAGE,
  });

  const statusLabel = (s: string) =>
    s === "ok"
      ? t.ok
      : s === "no_data"
        ? t.noData
        : s === "no_currency"
          ? t.noCurrency
          : s === "sync_disabled"
            ? t.syncDisabled
            : s === "fx_missing"
              ? t.fxMissing
              : t.stale;

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const from = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const to = Math.min(page * PER_PAGE, total);

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleSourceChange = (value: string) => {
    setPage(1);
    setSource(value);
  };

  const handleStatusChange = (value: string) => {
    setPage(1);
    setStatus(value);
  };

  const queryClient = useQueryClient();
  const [inspectItem, setInspectItem] = useState<TimeseriesInstrumentStatus | null>(null);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const handleSyncToggle = async (item: TimeseriesInstrumentStatus) => {
    const key = `${item.source}:${item.ticker}`;
    setTogglingKey(key);
    try {
      await setInstrumentSyncEnabled(item.source, item.ticker, !!item.syncDisabledAt);
      // Re-enabling resyncs the whole series, so the inspect dialog's points and
      // statistics are stale too.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: timeseriesAdminKeys.list }),
        queryClient.invalidateQueries({
          queryKey: timeseriesAdminKeys.data(item.source, item.ticker),
        }),
        queryClient.invalidateQueries({
          queryKey: timeseriesAdminKeys.statistics(item.source, item.ticker),
        }),
      ]);
    } catch {
      // List refetch below restores the real state.
    } finally {
      setTogglingKey(null);
    }
  };

  const fxMissingTotal = data?.fxMissingTotal ?? 0;

  return (
    <TooltipProvider delayDuration={0}>
      <div className="space-y-4">
        {fxMissingTotal > 0 && status !== "fx_missing" && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t.fxBannerTitle}</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-2">
              <span>{t.fxBannerBody(String(fxMissingTotal))}</span>
              <Button variant="outline" size="sm" onClick={() => handleStatusChange("fx_missing")}>
                {t.fxBannerShow}
              </Button>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Select value={source} onValueChange={handleSourceChange}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t.allSources}</SelectItem>
              {TIMESERIES_DATA_SOURCES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t.allStatuses}</SelectItem>
              {TIMESERIES_INSTRUMENT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {statusLabel(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <SearchInput
            className="w-72"
            placeholder={t.searchPlaceholder}
            value={searchInput}
            onValueChange={(value) => {
              setSearchInput(value);
              // The search applies on Enter or the button, but an emptied field
              // (the clear ×) should not leave the list filtered by the old query.
              if (!value) {
                setPage(1);
                setSearch("");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") applySearch();
            }}
          />
          <Button variant="outline" onClick={applySearch} disabled={isFetching}>
            {t.search}
          </Button>

          {total > 0 && (
            <span className="ml-auto text-sm text-muted-foreground">
              {t.showingResults({ from: String(from), to: String(to), total: String(total) })}
            </span>
          )}
        </div>

        {isLoading && items.length === 0 ? (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.instrument}</TableHead>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.currency}</TableHead>
                <TableHead>{t.status}</TableHead>
                <TableHead>{t.sync}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {["a", "b", "c", "d", "e"].map((key) => (
                <TableRow key={key}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton cells
                    <TableCell key={i}>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : total === 0 ? (
          <EmptyState>{t.noInstruments}</EmptyState>
        ) : (
          <Table
            variant="data"
            containerClassName={
              isFetching ? "opacity-50 pointer-events-none transition-opacity" : ""
            }
          >
            <TableHeader>
              <TableRow>
                <TableHead>{t.instrument}</TableHead>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.currency}</TableHead>
                <TableHead>{t.status}</TableHead>
                <TableHead>{t.sync}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={`${item.source}:${item.ticker}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <StatusDot item={item} lang={userLanguage} />
                      <code className="text-xs bg-primary/10 text-primary px-2 py-1 rounded whitespace-nowrap">
                        {item.source}:{item.ticker}
                      </code>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">{item.name}</TableCell>
                  <TableCell className="text-sm">{item.currency || "—"}</TableCell>
                  <TableCell>
                    <StatusBadges item={item} t={t} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {UNSYNCED_SOURCES.has(item.source) ? (
                        <span className="w-9 text-center text-muted-foreground">—</span>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Switch
                                checked={!item.syncDisabledAt}
                                disabled={togglingKey === `${item.source}:${item.ticker}`}
                                onCheckedChange={() => handleSyncToggle(item)}
                                aria-label={`${item.syncDisabledAt ? t.enableSync : t.disableSync}: ${item.name}`}
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {item.syncDisabledAt ? t.enableSync : t.disableSync}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            tooltip={t.viewChart}
                            disabled={item.pointCount === 0}
                            onClick={() => setInspectItem(item)}
                            aria-label={t.viewData}
                          >
                            <LineChart className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t.viewData}</TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <TimeseriesInspectDialog
          open={inspectItem !== null}
          item={inspectItem}
          onClose={() => setInspectItem(null)}
        />

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || isFetching}
            >
              {t.previous}
            </Button>
            <span className="text-sm text-muted-foreground">
              {t.pageOf(String(page), String(totalPages))}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || isFetching}
            >
              {t.next}
            </Button>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
